// admin/src/pages/Dashboard/components/NeedsAttentionCard.js
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Typography, Box, Chip } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SEVERITY_CONFIG } from "../dashboardConstants";

const getSeverityIcon = (severity, color) => {
  switch (severity) {
    case "critical":
      return <ErrorOutlineIcon sx={{ fontSize: 18, color }} />;
    case "high":
    case "warning":
      return <WarningAmberOutlinedIcon sx={{ fontSize: 18, color }} />;
    case "info":
    default:
      return <InfoOutlinedIcon sx={{ fontSize: 18, color }} />;
  }
};

const ACTION_ITEM_FALLBACK_LINKS = {
  "apps-mfa-off": "/applications?mfaRequired=false&status=Active",
  "devices-blocked-revoked": "/devices?status=blocked_or_revoked",
  "devices-stale": "/devices?status=stale",
  "users-no-mfa": "/user-management?mfa=disabled",
  "saml-cert-expiring": "/applications?authenticationProtocol=SAML",
  "agents-offline": "/windows-agent-devices?status=offline",
  // Retired: AD sync now runs through the gateway page
  "ad-sync-failure": "/admin/gateways",
  "sign-in-spike": "/home#signin-activity",
};

const resolveItemLink = (item) => {
  if (item.id === "sign-in-spike") return "/home#signin-activity";
  if (item.link && item.link.includes("?")) return item.link;
  if (ACTION_ITEM_FALLBACK_LINKS[item.id]) return ACTION_ITEM_FALLBACK_LINKS[item.id];
  // Retired: AD sync now runs through the gateway page
  if (item.link === "/ad-integration") return "/admin/gateways";
  if (item.link === "/dashboard/activity") return "/home#signin-activity";
  return item.link || "#";
};

const NeedsAttentionCard = ({ attention = [], T }) => {
  const count = attention.length;

  return (
    <div className="dl-card" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="dl-card-inner" style={{ padding: "20px 22px", flex: 1, display: "flex", flexDirection: "column" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <div>
            <div className="dl-eyebrow">Action Items</div>
            <Typography sx={{ fontFamily: T.fontHeading, fontSize: 16, fontWeight: 700, color: T.textPrimary }}>
              Needs Attention
            </Typography>
          </div>
          {count > 0 ? (
            <Chip
              label={`${count} Action${count > 1 ? "s" : ""}`}
              size="small"
              sx={{
                background: `${T.accent3}18`,
                color: T.accent3,
                fontWeight: 700,
                fontSize: 11,
                border: `1px solid ${T.accent3}30`,
              }}
            />
          ) : (
            <Chip
              label="Optimal"
              size="small"
              sx={{
                background: `${T.accent2}18`,
                color: T.accent2,
                fontWeight: 700,
                fontSize: 11,
                border: `1px solid ${T.accent2}30`,
              }}
            />
          )}
        </Box>

        {count === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            py={4}
            textAlign="center"
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: `${T.accent2}14`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 10,
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 24, color: T.accent2 }} />
            </div>
            <Typography sx={{ fontFamily: T.fontHeading, fontSize: 14, fontWeight: 700, color: T.textPrimary }}>
              All Systems Healthy
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: 12, color: T.textMuted, mt: 0.5, maxWidth: 220 }}>
              No critical policies, certificates, or devices requiring administrator action.
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.25} flex={1} sx={{ overflowY: "auto", maxHeight: 310 }}>
            {attention.map((item, idx) => {
              const resolvedLink = resolveItemLink(item);
              const sev = SEVERITY_CONFIG[item.severity] || SEVERITY_CONFIG.warning;

              const handleClick = (e) => {
                if (
                  item.id === "sign-in-spike" ||
                  resolvedLink.includes("signin-activity") ||
                  resolvedLink.includes("/dashboard/activity")
                ) {
                  e.preventDefault();
                  window.dispatchEvent(
                    new CustomEvent("dashboard:navigate-tab", {
                      detail: { tab: 2, sectionId: "signin-activity" },
                    })
                  );
                  const el = document.getElementById("signin-activity");
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }
              };

              return (
                <Box
                  key={item.id || idx}
                  component={RouterLink}
                  to={resolvedLink}
                  onClick={handleClick}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    background: T.bgMuted,
                    border: `1px solid ${T.border}`,
                    textDecoration: "none",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      background: `${T.accent1}0A`,
                      borderColor: T.accent1,
                      transform: "translateX(2px)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 6,
                        background: sev.bgLight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {getSeverityIcon(item.severity, sev.color)}
                    </div>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        noWrap
                        sx={{
                          fontFamily: T.fontBody,
                          fontSize: 12,
                          fontWeight: 600,
                          color: T.textPrimary,
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: 11, color: sev.color, textTransform: "capitalize" }}>
                        {sev.label} Priority
                      </Typography>
                    </Box>
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 18, color: T.textMuted, ml: 1, flexShrink: 0 }} />
                </Box>
              );
            })}
          </Box>
        )}
      </div>
    </div>
  );
};

export default React.memo(NeedsAttentionCard);
